import { Box, Grid, IconButton, Theme, alpha, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import testimonials_background from '../../assets/landing_page/testimonials-bg.webp';
import netcare_logo from '../../assets/client_logos/netcare-logo.webp';
import growthpoint_logo from '../../assets/client_logos/growthpoint-logo.webp';
import cch_logo from '../../assets/client_logos/cch-logo.webp';
import kal_group_logo from '../../assets/client_logos/kal-group-logo.webp';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { MinorHeading, Subheading } from '../shared/typography/PrimaryTypography';

interface Testimonial {
  client: string;
  logo: string;
  quote: string | React.ReactNode;
  name: string;
  title: string;
}

const testimonials: Array<Testimonial> = [
  {
    client: 'Netcare',
    logo: netcare_logo,
    quote: (
      <>
        “Energy Partners has offered <strong>innovative insight</strong> and support into{' '}
        <strong>visualising, tracking</strong> and
        <strong> benchmarking utility consumption</strong> for the Netcare Energy portfolio as a
        group as well as identifying implementation projects related to{' '}
        <strong>improving efficiency, reducing consumption, saving costs</strong> and{' '}
        <strong>reducing risk</strong>. Thus far Netcare has achieved a{' '}
        <strong>34% total reduction in energy consumption</strong> per bed and is well on track to
        meet its 2030 targets. Clearly, within these long-term objectives, we needed a{' '}
        <strong>credible partner</strong> who could provide expert support, relying on the{' '}
        <strong>data integrity driven platform</strong>.
        <br />
        <br />
        <b>
          We value EP’s expertise and their commitment to driving impactful, sustainable solutions
          in South Africa.
        </b>
        ”
      </>
    ),
    name: 'Andre Nortje',
    title: 'National Environmental Sustainability Manager, Netcare Ltd'
  },
  {
    client: 'Growthpoint Properties',
    logo: growthpoint_logo,
    quote: (
      <>
        “We face significant challenges such as rising utility costs and infrastructure limitations
        that can impact our operations and client satisfaction. To address these challenges, we
        partnered with Energy Partners. Their <strong>expertise</strong> in{' '}
        <strong>energy efficiency</strong> and <strong>alternative energy solutions</strong> has
        been invaluable in helping us <strong>reduce energy consumption, lower costs,</strong> and
        <strong> improve operational efficiency</strong>.
        <br />
        <br />
        <b>
          By implementing innovative solutions and optimising our building management systems, we
          are making significant strides towards a more sustainable future
        </b>
        .”
      </>
    ),
    name: 'Martjie Cloete',
    title: 'Sustainability Manager: Projects, Growthpoint Properties'
  },
  {
    client: 'CCH',
    logo: cch_logo,
    quote: (
      <>
        “Our group companies measured <strong>ESG data</strong> in completely different formats,
        often manually in spreadsheets. Consolidating this data into a usable format for
        decision-making or external reporting was tedious. Energy Partners{' '}
        <strong>helped us bring everything together</strong> electronically and very close to real
        time, making <strong>reporting easier and simpler</strong>, and we know we can{' '}
        <strong>trust the information</strong>.”
      </>
    ),
    name: 'Jurie Schoeman',
    title: 'Chief Operations Officer, Commercial Cold Holdings'
  },
  {
    client: 'KAL Group',
    logo: kal_group_logo,
    quote: (
      <>
        “What we as a business particularly valued were the{' '}
        <strong>achievable opportunities identified</strong> by Energy Partners - opportunities that
        delivered <strong>tangible financial benefits</strong>, especially in terms of{' '}
        <strong>energy cost savings</strong>.
        <br />
        <br />
        One of the most significant advantages of our relationship has been the{' '}
        <strong>integrity of the data</strong> and the <strong>accuracy of the figures</strong>{' '}
        Agrimark is able to report. This has significantly{' '}
        <strong>enhanced the credibility </strong>
        of all reporting outputs we share.”
      </>
    ),
    name: 'Jan Crafford',
    title: 'Executive Manager: Business Development, Agrimark Operations'
  }
];

/**
 * Section displaying testimonials from satisfied clients of Syntiro and EP Sustainability.
 * @returns {JSX.Element}
 */
const TestimonialsSection = () => {
  const [testimonialIndex, setTestimonialIndex] = React.useState<number>(0);
  const theme: Theme = useTheme();
  const mobile_viewport: boolean = useMediaQuery('(max-width:600px)');
  const current_testimonial: Testimonial = testimonials[testimonialIndex];

  const handlePrevTestimonial = (): void => {
    setTestimonialIndex(testimonialIndex === 0 ? testimonials.length - 1 : testimonialIndex - 1);
  };

  const handleNextTestimonial = (): void => {
    setTestimonialIndex(testimonialIndex === testimonials.length - 1 ? 0 : testimonialIndex + 1);
  };

  // Auto-cycle testimonial every 20 seconds.
  React.useEffect(() => {
    const interval: NodeJS.Timeout = setInterval(() => {
      setTestimonialIndex((prev_index: number) => (prev_index + 1) % testimonials.length);
    }, 20000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        backgroundImage: { xs: 'none', md: `url(${testimonials_background})` },
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundSize: '1400px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: alpha(theme.palette.secondary.dark, 0.1),
        py: 4
      }}
      id="testimonials"
    >
      <Grid
        container
        spacing={2}
        sx={{ alignItems: 'flex-start', justifyContent: 'center', my: 2 }}
      >
        <Grid size={{ md: 1 }} sx={{ pt: { xs: 2, md: 12 }, order: { xs: 2, md: 1 } }}>
          <IconButton onClick={handlePrevTestimonial}>
            <ArrowBackIosNewIcon color="secondary" fontSize="large" />
          </IconButton>
        </Grid>
        <Grid
          size={{ xs: 12, md: 10 }}
          sx={{
            px: { xs: 2, lg: 12 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            order: { xs: 1, md: 2 }
          }}
        >
          {current_testimonial && (
            <>
              <Box
                sx={{
                  width: { xs: '70%', md: '50%' },
                  borderBottom: `1px solid ${theme.palette.teal.main}`,
                  pb: 2
                }}
              >
                <img
                  style={{ height: '100%', maxHeight: 125, maxWidth: '100%' }}
                  src={current_testimonial.logo}
                  alt={current_testimonial.client}
                />
              </Box>
              {mobile_viewport ? (
                <MinorHeading
                  sx={{
                    my: 6,
                    lineHeight: 1.7,
                    fontWeight: theme.typography.fontWeightRegular,
                    textAlign: 'left'
                  }}
                >
                  {current_testimonial.quote}
                </MinorHeading>
              ) : (
                <Subheading
                  sx={{ my: 6, lineHeight: 1.7, fontWeight: theme.typography.fontWeightRegular }}
                >
                  {current_testimonial.quote}
                </Subheading>
              )}
              <MinorHeading
                weight="Bold"
                sx={{ color: theme.palette.teal.main, textAlign: { xs: 'left', sm: 'center' } }}
              >
                {current_testimonial.name}, {current_testimonial.title}
              </MinorHeading>
            </>
          )}
        </Grid>
        <Grid size={{ md: 1 }} sx={{ pt: { xs: 2, md: 12 }, order: 3 }}>
          <IconButton onClick={handleNextTestimonial}>
            <ArrowForwardIosIcon color="secondary" fontSize="large" />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TestimonialsSection;
